/* eslint-disable no-template-curly-in-string */
/* eslint-disable max-len */
import React from "react";
import { connect } from "react-redux";
import get from "lodash/get";
import { Formik } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import { YesNoList } from "../../../../../constants/options";
import { MAX_EMAIL_LENGTH } from "../../../../../constants";
import { getSignatories } from "../../../../../store/selectors/appConfig";

import {
  InlineRadioGroup,
  SelectAutocomplete,
  AutoSaveField as Field,
  Input
} from "../../../../../components/Form";

import { SubmitButton } from "../../../components/SubmitButton/SubmitButton";
import { getInvalidMessage, getNotTrimmedMessage } from "../../../../../utils/getValidationMessage";
import { checkIsTrimmed, SPECIAL_CHARACTERS_REGEX } from "../../../../../utils/validation";
import { InfoNoteStyled, FormStyle } from "../styled";
import { ScrollToError } from "components/ScrollToError";

const signatoryRightsSchema = Yup.object().shape({
  personalAssetsInUAE: Yup.string().nullable(),
  secondaryEmail: Yup.string()
    .nullable()
    .email(getInvalidMessage("Secondary Email"))
    .max(50, "Maximum 50 characters allowed"),
  nameOfOrganisation: Yup.string()
    .nullable()
    .max(30, "Maximum ${max} characters allowed")
    .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("Name of organization"))
    .test("space validation", getNotTrimmedMessage("Name of organization"), checkIsTrimmed),
  designation: Yup.string(),
  numberOfYearsWorked: Yup.string(),
  backgroundOfSignatory: Yup.string()
    .nullable()
    .max(255, "Maximum ${max} characters allowed"),
  familyOwnedBusiness: Yup.string()
});

const initialValues = {
  personalAssetsInUAE: "",
  secondaryEmail: "",
  nameOfOrganisation: "",
  designation: "",
  numberOfYearsWorked: "",
  backgroundOfSignatory: "",
  familyOwnedBusiness: ""
};

const ShareholdingStep = ({
  handleContinue,
  index,
  stakeholders,
  createFormChangeHandler,
  isShareholder,
  stakeholder: { fullName }
}) => {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleContinue}
      validationSchema={signatoryRightsSchema}
      validateOnChange={false}
    >
      {createFormChangeHandler(({ values, setFieldValue }) => {
        const signatoryPath = `roProspect.signatoryInfo[${index}]`;
        return (
          <FormStyle>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                {!isShareholder && (
                  <InfoNoteStyled text={`${fullName || "This person"} is not a shareholder`} />
                )}
                <h5 className="mb-0"> Stakeholders </h5>
              </Grid>

              <Grid item sm={12} xs={12}>
                <Field
                  name="personalAssetsInUAE"
                  placeholder="Personal assets in UAE"
                  disabled={!isShareholder}
                  label="Personal assets in UAE"
                  path={`${signatoryPath}.personalAssetsInUAE`}
                  component={SelectAutocomplete}
                  datalistId="personalAssetEstimateSA"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="secondaryEmail"
                  placeholder="Secondary Email ID ( if any)"
                  disabled={!isShareholder}
                  path={`${signatoryPath}.secondaryEmail`}
                  component={Input}
                  InputProps={{
                    inputProps: { maxLength: MAX_EMAIL_LENGTH, tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <h5 className="mb-0"> Latest Work Experience </h5>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="nameOfOrganisation"
                  label="Name of organisation"
                  disabled={!isShareholder}
                  placeholder="Name of organisation"
                  path={`${signatoryPath}.shareholderWorkExperience.companyName`}
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="designation"
                  label="Designation"
                  disabled={!isShareholder}
                  placeholder="Designation"
                  path={`${signatoryPath}.shareholderWorkExperience.designation`}
                  component={SelectAutocomplete}
                  datalistId="individualOccupation"
                  tabIndex="0"
                  isSearchable
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="numberOfYearsWorked"
                  label="Number of years worked"
                  disabled={!isShareholder}
                  path={`${signatoryPath}.shareholderWorkExperience.noOfYearsWorked`}
                  component={SelectAutocomplete}
                  datalistId="numYearsWorkedSA"
                  tabIndex="0"
                  isSearchable
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="backgroundOfSignatory"
                  placeholder="Background of Stakeholders"
                  disabled={!isShareholder}
                  path={`${signatoryPath}.shareholderWorkExperience.backgroundInfo`}
                  multiline
                  rows="4"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={8} xs={8}>
                <Field
                  name="familyOwnedBusiness"
                  path={`${signatoryPath}.shareholderWorkExperience.familyOwnedBusiness`}
                  disabled={!isShareholder}
                  component={InlineRadioGroup}
                  options={YesNoList}
                  label="Family owned business?"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
            </Grid>
            <SubmitButton />
          </FormStyle>
        );
      })}
    </Formik>
  );
};

const mapStateToProps = (state, { index }) => ({
  isShareholder: get(getSignatories(state)[index], "isShareholder", false)
});

export const Shareholding = connect(mapStateToProps)(ShareholdingStep);
